import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertCircle,
  CircuitBoard,
  Clock,
  Cpu,
  Monitor,
  RefreshCw,
  Thermometer,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react";
import useHeartbeatStatus from "../../hooks/useHeartbeatStatus";

const STATION_LAYOUT = [
  "1_yin", "1_yang",
  "2_yin", "2_yang",
  "3_yin", "3_yang",
  "4_yin", "4_yang",
  "5_yin", "5_yang",
  "6_yin", "6_yang",
  "7_yin", "7_yang",
  "8_yin", "8_yang",
  "9_yin", "9_yang",
  "10_yin", "10_yang",
  "11_yin", "11_yang",
  "12_yin", "12_yang",
  "13_yin", "13_yang",
  "14_yin", "14_yang",
  "15_yin", "15_yang",
  "16_yin", "16_yang",
  "17_yin", "17_yang",
  "18_yin", "18_yang",
  "19_yin", "19_yang",
  "20_yin", "20_yang",
  "21_yin", "21_yang",
  "22_yin", "22_yang",
  "23_yin", "23_yang",
  "24_yin", "24_yang",

];

// battery / temperature readings older than this are shown as stale, not current
const TELEMETRY_STALE_SECONDS = 60;

const relativeTime = (unixSeconds, nowMs) => {
  if (!unixSeconds) return null;
  const s = Math.max(0, Math.round(nowMs / 1000 - unixSeconds));
  if (s < 10) return "Just now";
  if (s < 60) return `${Math.floor(s / 5) * 5}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
};

const fmtTemp = (v) => (typeof v === "number" ? v.toFixed(1) : null);

// battery thresholds: > 50 green, 20–50 amber, < 20 red
const batteryTone = (pct) => {
  if (typeof pct !== "number") return { ring: "text-neutral-300", text: "text-neutral-400", tile: "bg-neutral-300" };
  if (pct > 50) return { ring: "text-green-500", text: "text-green-700", tile: "bg-green-500" };
  if (pct >= 20) return { ring: "text-amber-500", text: "text-amber-700", tile: "bg-amber-500" };
  return { ring: "text-red-500", text: "text-red-700", tile: "bg-red-500" };
};

// Android BatteryManager health values
const HEALTH = {
  good: { label: "Healthy", dot: "bg-green-500", text: "text-green-700" },
  cold: { label: "Cold", dot: "bg-amber-500", text: "text-amber-700" },
  overheat: { label: "Overheat", dot: "bg-red-500", text: "text-red-700" },
  dead: { label: "Dead", dot: "bg-red-500", text: "text-red-700" },
  over_voltage: { label: "Over voltage", dot: "bg-red-500", text: "text-red-700" },
  unspecified_failure: { label: "Failure", dot: "bg-red-500", text: "text-red-700" },
};

// everything a connector panel needs, derived from the heartbeat response
const connectorView = (key, status, heartbeats, now) => {
  const device = heartbeats?.devices?.[key];
  const displaySeen = heartbeats?.lastSeen?.display?.[key] || null;
  const espSeen = heartbeats?.lastSeen?.esp?.[key] || null;
  const age = device?.received_at ? now / 1000 - device.received_at : null;
  const stale = age !== null && age > TELEMETRY_STALE_SECONDS;
  const pct = typeof device?.battery_percent === "number" ? Math.max(0, Math.min(100, device.battery_percent)) : null;
  const health = device?.battery_health
    ? HEALTH[device.battery_health] || { label: device.battery_health.replace(/_/g, " "), dot: "bg-neutral-400", text: "text-neutral-600" }
    : null;

  // online: the latest heartbeat; otherwise when the failing part was last heard from
  let heartbeatLabel = "Heartbeat";
  let heartbeat = relativeTime(Math.max(displaySeen || 0, espSeen || 0) || null, now);
  if (!status.displayAlive && status.espAlive) {
    heartbeatLabel = "Display seen";
    heartbeat = relativeTime(displaySeen, now);
  } else if (status.displayAlive && !status.espAlive) {
    heartbeatLabel = "ESP seen";
    heartbeat = relativeTime(espSeen, now);
  }

  const lowBattery = !stale && pct !== null && pct < 20;
  const unhealthy = !stale && device?.battery_health && device.battery_health !== "good";
  return {
    key,
    ...status,
    device,
    stale,
    pct,
    health,
    heartbeat,
    heartbeatLabel,
    attention: !status.available || lowBattery || unhealthy,
  };
};

export default function HeartbeatDashboard() {
  const { heartbeats, isLoading, error, updatedAt, refetch } = useHeartbeatStatus();
  const [filter, setFilter] = useState("all");
  const [highlight, setHighlight] = useState(null);
  const panelRefs = useRef({});

  // re-render so "x seconds ago" stays current between polls
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, []);

  const normalizedHeartbeats = STATION_LAYOUT.reduce((acc, key) => {
    const displayAlive = heartbeats?.display?.[key] === true;
    const espAlive = heartbeats?.esp?.[key] === true;

    acc[key] = {
      available: displayAlive && espAlive,
      displayAlive,
      espAlive,
    };

    return acc;
  }, {});

  const active = Object.values(normalizedHeartbeats).filter(
    (v) => v.available
  ).length;

  const total = STATION_LAYOUT.length;
  const availabilityPct = Math.round((active / total) * 100);

  // group connectors by station, keeping the layout order
  const stations = useMemo(() => {
    const out = [];
    const byId = {};
    STATION_LAYOUT.forEach((key) => {
      const [station] = key.split("_");
      if (!byId[station]) {
        byId[station] = { id: station, connectors: [] };
        out.push(byId[station]);
      }
      byId[station].connectors.push(connectorView(key, normalizedHeartbeats[key], heartbeats, now));
    });
    return out;
    // normalizedHeartbeats is derived from heartbeats
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heartbeats, now]);

  const attentionCount = stations.filter((s) => s.connectors.some((c) => c.attention)).length;
  const lowBatteryCount = stations.reduce((n, s) => n + s.connectors.filter((c) => !c.stale && c.pct !== null && c.pct < 20).length, 0);
  const shown = filter === "attention" ? stations.filter((s) => s.connectors.some((c) => c.attention)) : stations;

  const jumpTo = (stationId) => {
    setFilter("all");
    setHighlight(stationId);
    requestAnimationFrame(() => panelRefs.current[stationId]?.scrollIntoView({ behavior: "smooth", block: "center" }));
    setTimeout(() => setHighlight((h) => (h === stationId ? null : h)), 1800);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-neutral-800 to-neutral-950 text-white shadow-sm">
            <Activity className="h-5 w-5" />
            <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 motion-safe:animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-neutral-50" />
            </span>
          </div>
          <div>
            <h1 className="page-title">Heartbeat Monitor</h1>
            <p className="page-subtitle">Display + ESP combined availability, battery and device health</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {updatedAt && (
            <span className="text-xs text-neutral-500">
              Updated {updatedAt.toLocaleTimeString("en-IN")}
            </span>
          )}
          <button onClick={refetch} disabled={isLoading} className="btn btn-secondary text-[13px] disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
            {isLoading ? "Refreshing" : "Refresh"}
          </button>
        </div>
      </header>

      {/* Fleet overview: summary + map of every connector */}
      <section className="card overflow-hidden">
        <div className="grid grid-cols-3 divide-x divide-neutral-100">
          <Kpi label="Active" value={`${active} / ${total}`} tone="text-green-700" />
          <Kpi label="Availability" value={`${availabilityPct}%`}>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-100">
              <div className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-700" style={{ width: `${availabilityPct}%` }} />
            </div>
          </Kpi>
          <Kpi label="Inactive" value={total - active} tone={total - active ? "text-red-700" : "text-ink"} />
        </div>

        <div className="border-t border-neutral-100 bg-neutral-50/60 px-4 py-3 sm:px-5">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Fleet map</p>
            <div className="flex items-center gap-3 text-[11px] text-neutral-500">
              <Legend className="bg-green-500" label="Online" />
              <Legend className="bg-amber-400" label="Low battery" />
              <Legend className="bg-red-500" label="Offline" />
            </div>
          </div>
          <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-8 md:grid-cols-12 xl:grid-cols-[repeat(24,minmax(0,1fr))]">
            {stations.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => jumpTo(s.id)}
                className="group flex flex-col items-center gap-1 rounded-md p-1 transition hover:bg-white hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                title={s.connectors
                  .map((c) => `${s.id} ${c.key.split("_")[1].toUpperCase()}: ${c.available ? "online" : "offline"}${c.pct !== null && !c.stale ? `, ${Math.round(c.pct)}%` : ""}`)
                  .join("\n")}
              >
                <span className="flex w-full gap-0.5">
                  {s.connectors.map((c) => (
                    <span
                      key={c.key}
                      className={`h-3.5 flex-1 rounded-[3px] transition-transform group-hover:scale-y-110 ${
                        !c.available ? "bg-red-500" : !c.stale && c.pct !== null && c.pct < 20 ? "bg-amber-400" : "bg-green-500"
                      }`}
                    />
                  ))}
                </span>
                <span className="text-[10px] font-medium tabular-nums text-neutral-500 group-hover:text-ink">{s.id}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-0.5 text-[13px] font-medium shadow-sm">
          {[
            { id: "all", label: `All stations`, count: stations.length },
            { id: "attention", label: "Needs attention", count: attentionCount },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 transition ${
                filter === f.id ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-ink"
              }`}
            >
              {f.label}
              <span className={`rounded px-1.5 text-[11px] tabular-nums ${filter === f.id ? "bg-white/15" : "bg-neutral-100 text-neutral-600"}`}>{f.count}</span>
            </button>
          ))}
        </div>
        {lowBatteryCount > 0 && (
          <p className="text-[12px] text-amber-700">
            {lowBatteryCount} connector{lowBatteryCount === 1 ? "" : "s"} below 20% battery
          </p>
        )}
      </div>

      {/* Stations */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2 min-[1800px]:grid-cols-3">
        {shown.map((s) => (
          <StationPanel key={s.id} station={s} highlighted={highlight === s.id} panelRef={(el) => (panelRefs.current[s.id] = el)} />
        ))}
        {!shown.length && (
          <div className="card col-span-full p-10 text-center text-sm text-neutral-500">Every station is online with a healthy battery.</div>
        )}
      </section>
    </div>
  );
}

/* ================= Components ================= */

function Kpi({ label, value, tone = "text-ink", children }) {
  return (
    <div className="px-4 py-3 sm:px-5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{label}</p>
      <p className={`mt-0.5 text-xl font-semibold tabular-nums sm:text-2xl ${tone}`}>{value}</p>
      {children}
    </div>
  );
}

const Legend = ({ className, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className={`h-2 w-2 rounded-[2px] ${className}`} />
    {label}
  </span>
);

function StationPanel({ station, highlighted, panelRef }) {
  const online = station.connectors.filter((c) => c.available).length;
  const all = station.connectors.length;
  const state = online === all ? "online" : online === 0 ? "offline" : "partial";
  const accent = {
    online: { bar: "from-green-500/80 to-emerald-400/60", led: "bg-green-500", ping: "bg-green-400", text: "text-green-700", label: "All online" },
    partial: { bar: "from-amber-500/80 to-amber-300/60", led: "bg-amber-500", ping: null, text: "text-amber-700", label: "Partially online" },
    offline: { bar: "from-red-500/80 to-red-400/60", led: "bg-red-500", ping: null, text: "text-red-700", label: "Offline" },
  }[state];

  return (
    <article
      ref={panelRef}
      className={`group relative overflow-hidden rounded-xl border bg-white shadow-card transition duration-200 motion-safe:hover:-translate-y-0.5 hover:shadow-md ${
        highlighted ? "border-brand-400 ring-2 ring-brand-200" : "border-neutral-200/80"
      }`}
    >
      {/* status accent */}
      <div className={`absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r ${accent.bar}`} />

      {/* Station identity */}
      <header className="flex items-center justify-between gap-3 bg-gradient-to-b from-neutral-50 to-white px-4 pb-3 pt-4">
        <div className="flex items-center gap-3">
          <div className="relative flex h-11 w-11 flex-col items-center justify-center rounded-lg bg-gradient-to-br from-neutral-800 to-neutral-950 text-white shadow-sm">
            <span className="text-[8px] font-semibold uppercase leading-none tracking-[0.18em] text-neutral-400">Stn</span>
            <span className="text-lg font-bold leading-tight tabular-nums">{station.id.padStart(2, "0")}</span>
          </div>
          <div>
            <p className="text-[15px] font-semibold leading-tight text-ink">Station {station.id}</p>
            <p className="mt-0.5 flex items-center gap-1 text-[12px] text-neutral-500">
              <Zap className="h-3 w-3" />
              {all} connector{all === 1 ? "" : "s"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            {accent.ping && <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 motion-safe:animate-ping ${accent.ping}`} />}
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${accent.led}`} />
          </span>
          <span className={`text-[12px] font-semibold ${accent.text}`}>{accent.label}</span>
          <span className="rounded-md bg-neutral-100 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-neutral-600">
            {online}/{all}
          </span>
        </div>
      </header>

      {/* Connectors side by side */}
      <div className="grid grid-cols-1 divide-y divide-neutral-100 border-t border-neutral-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        {station.connectors.map((c) => (
          <ConnectorPane key={c.key} c={c} />
        ))}
      </div>
    </article>
  );
}

function ConnectorPane({ c }) {
  const gun = c.key.split("_")[1] || "";
  let reason = null;
  if (!c.displayAlive && !c.espAlive) reason = "Display + ESP down";
  else if (!c.displayAlive) reason = "Display down";
  else if (!c.espAlive) reason = "ESP down";
  const tone = batteryTone(c.stale ? null : c.pct);
  const bt = fmtTemp(c.device?.battery_temp_c);
  const dt = fmtTemp(c.device?.device_temp_c);

  return (
    <div className={`flex flex-col gap-3 p-4 transition-colors ${c.available ? "group-hover:bg-neutral-50/40" : "bg-red-50/40"}`}>
      {/* connector + online state */}
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-700">
          <span className={`h-1.5 w-1.5 rounded-full ${c.available ? "bg-green-500" : "bg-red-500"}`} />
          {gun}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            c.available ? "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/15" : "bg-red-100/70 text-red-700 ring-1 ring-inset ring-red-600/20"
          }`}
        >
          {c.available ? "Online" : "Offline"}
        </span>
      </div>

      {/* battery gauge + metrics */}
      <div className="flex items-center gap-4">
        <div className="flex flex-col items-center">
          <BatteryRing pct={c.pct} stale={c.stale} tone={tone} />
          {c.health && !c.stale ? (
            <span className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium capitalize ${c.health.text}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${c.health.dot}`} />
              {c.health.label}
            </span>
          ) : (
            <span className="mt-1.5 text-[11px] text-neutral-400">{c.stale ? "Stale" : "No health data"}</span>
          )}
        </div>

        <dl className={`min-w-0 flex-1 space-y-2 text-[12px] ${c.stale ? "opacity-60" : ""}`}>
          <MetricRow icon={Thermometer} label="Battery" value={c.stale ? null : bt} unit="°C" />
          <MetricRow icon={Cpu} label="Device" value={c.stale ? null : dt} unit="°C" />
          <MetricRow icon={Clock} label={c.heartbeatLabel} value={c.heartbeat} warn={!c.available} />
        </dl>
      </div>

      {/* display / ESP health */}
      <div className="grid grid-cols-2 gap-2">
        <HealthIndicator icon={Monitor} label="Display" ok={c.displayAlive} />
        <HealthIndicator icon={CircuitBoard} label="ESP" ok={c.espAlive} />
      </div>

      {/* footnote */}
      <div className="flex min-h-[16px] items-center gap-3 text-[11px] text-neutral-500">
        {reason && <span className="font-semibold text-red-700">{reason}</span>}
        {!reason && c.stale && <span className="font-medium text-amber-700">Battery data last reported {relativeTime(c.device.received_at, Date.now())}</span>}
        {!reason && !c.stale && !c.device && <span>No battery data reported</span>}
        {!reason && !c.stale && c.device && (
          <>
            {c.device.app_version && <span className="tabular-nums">App v{c.device.app_version}</span>}
            {typeof c.device.wifi_connected === "boolean" && (
              <span className="inline-flex items-center gap-1">
                {c.device.wifi_connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3 text-red-600" />}
                {c.device.wifi_connected ? (typeof c.device.rssi === "number" ? `${c.device.rssi} dBm` : "Wi-Fi") : "No Wi-Fi"}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function BatteryRing({ pct, stale, tone, size = 84 }) {
  const stroke = 7;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const shown = stale ? null : pct;
  const offset = shown === null ? circumference : circumference * (1 - shown / 100);
  return (
    <div className="relative" style={{ width: size, height: size }} role="meter" aria-label="Battery" aria-valuemin={0} aria-valuemax={100} aria-valuenow={shown ?? undefined}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-neutral-100" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          stroke="currentColor"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`${tone.ring} transition-[stroke-dashoffset] duration-700 ease-out`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {shown === null ? (
          <span className="text-[15px] font-semibold text-neutral-400">N/A</span>
        ) : (
          <span className="text-[20px] font-bold leading-none tabular-nums text-ink">
            {Math.round(shown)}
            <span className="text-[11px] font-semibold text-neutral-500">%</span>
          </span>
        )}
        <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-neutral-400">Battery</span>
      </div>
    </div>
  );
}

function MetricRow({ icon: Icon, label, value, unit, warn }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="flex min-w-0 items-center gap-1.5 text-neutral-500">
        <Icon className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
        <span className="truncate">{label}</span>
      </dt>
      <dd className={`whitespace-nowrap font-semibold tabular-nums ${value == null ? "text-neutral-400" : warn ? "text-red-700" : "text-ink"}`}>
        {value == null ? "N/A" : (
          <>
            {value}
            {unit && <span className="ml-0.5 text-[11px] font-medium text-neutral-500">{unit}</span>}
          </>
        )}
      </dd>
    </div>
  );
}

function HealthIndicator({ icon: Icon, label, ok }) {
  return (
    <div
      className={`flex items-center justify-between rounded-md px-2 py-1.5 text-[11px] font-semibold transition-colors ${
        ok ? "bg-green-50/80 text-green-800" : "bg-red-50 text-red-700"
      }`}
    >
      <span className="inline-flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 opacity-70" />
        {label}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-green-500" : "bg-red-500"}`} />
        {ok ? "OK" : "Down"}
      </span>
    </div>
  );
}
